import { useEffect, useRef } from 'react'
import './PlatformArchitectureSection.css'

export default function PlatformArchitectureSection() {
  const sectionRef = useRef(null)
  const triggered = useRef(false)

  useEffect(() => {
    const fade = (id: string, delay: number) => setTimeout(() => {
      const el = document.getElementById(id)
      if (el) { el.style.transition = 'opacity 0.5s ease'; el.style.opacity = '1' }
    }, delay)

    const drawArc = (id: string, delay: number) => setTimeout(() => {
      const el = document.getElementById(id)
      if (el) {
        el.style.transition = 'stroke-dashoffset 0.7s cubic-bezier(0.4,0,0.2,1)'
        el.style.strokeDashoffset = '0'
      }
    }, delay)

    const run = () => {
      if (triggered.current) return
      triggered.current = true
      fade('pa-hire-block', 200)
      fade('pa-connector', 600)
      fade('pa-loop-label', 900)
      fade('pa-node-assess', 900);  drawArc('pa-arc1', 900)
      fade('pa-node-develop', 1300); drawArc('pa-arc2', 1300)
      fade('pa-node-assist', 1700);  drawArc('pa-arc3', 1700)
      fade('pa-lbl1', 1100)
      fade('pa-lbl2', 1500)
      fade('pa-lbl3', 1900)
      fade('pa-caption', 2200)
    }

    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) run()
    }, { threshold: 0.25 })

    const el = sectionRef.current
    if (el) io.observe(el)
    return () => io.disconnect()
  }, [])

  return (
    <section ref={sectionRef} className="pa-section" id="platform-architecture">
      <div className="container">

        {/* ── Header ── */}
        <div className="pa-header">
          <span className="pa-eyebrow">THE INTELLIGENCE LOOP</span>
          <h2>Hire Once. Develop Continuously.</h2>
          <p>SmartHire brings the right person in. Then three AI modules work together — continuously — across their entire career.</p>
        </div>

        {/* ── Diagram ── */}
        <div className="pa-diagram-wrap">

          {/* LEFT: SmartHire Entry Gateway */}
          <div className="pa-hire-block" id="pa-hire-block">
            <div className="pa-one-time">ONE-TIME ENTRY</div>
            <div className="pa-hire-card">
              <div className="pa-hire-card-icon">🎯</div>
              <div className="pa-hire-card-name">SmartHire</div>
              <div className="pa-hire-card-sub">Interview AI</div>
              <div className="pa-hire-card-features">
                ✓ Conversational interview<br/>
                ✓ Real capability assessment<br/>
                ✓ Hire decision intelligence
              </div>
              <div className="pa-hire-label">Candidate → Employee</div>
            </div>
          </div>

          {/* CONNECTOR */}
          <div className="pa-connector" id="pa-connector">
            <div className="pa-connector-label">joins<br/>org</div>
            <svg width="60" height="20" viewBox="0 0 60 20">
              <defs>
                <marker id="pa-ah" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
                  <polygon points="0 0, 8 3, 0 6" fill="#c9a84c"/>
                </marker>
              </defs>
              <line x1="4" y1="10" x2="50" y2="10" stroke="#c9a84c" strokeWidth="2.5" markerEnd="url(#pa-ah)" strokeLinecap="round"/>
            </svg>
            <div className="pa-connector-label">✓ Hired</div>
          </div>

          {/* RIGHT: 3-Product Employee Loop */}
          <div className="pa-loop-area">
            <div className="pa-loop-glow"/>

            {/* Loop badge */}
            <div className="pa-loop-label" id="pa-loop-label">
              <span className="pa-loop-badge">
                <span className="pa-spin">↻</span>
                Continuous Employee Loop
              </span>
            </div>

            {/* SVG Triangle */}
            <div className="pa-svg-wrap">
              <svg viewBox="0 0 400 300">
                <defs>
                  <linearGradient id="pa-lg1" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#c9a84c"/>
                    <stop offset="100%" stopColor="#e0bc6e" stopOpacity="0.5"/>
                  </linearGradient>
                  <linearGradient id="pa-lg2" x1="100%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#c9a84c"/>
                    <stop offset="100%" stopColor="#e0bc6e" stopOpacity="0.5"/>
                  </linearGradient>
                  <linearGradient id="pa-lg3" x1="100%" y1="100%" x2="0%" y2="0%">
                    <stop offset="0%" stopColor="#c9a84c"/>
                    <stop offset="100%" stopColor="#e0bc6e" stopOpacity="0.5"/>
                  </linearGradient>
                  <marker id="pa-am" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
                    <polygon points="0 0, 7 3.5, 0 7" fill="#c9a84c"/>
                  </marker>
                  <filter id="pa-glow">
                    <feGaussianBlur stdDeviation="2.5" result="b"/>
                    <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
                  </filter>
                  <linearGradient id="pa-nodeGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#1c2b3a"/>
                    <stop offset="100%" stopColor="#2e4057"/>
                  </linearGradient>
                </defs>

                {/* Faint background triangle */}
                <polygon points="200,30 360,240 40,240" fill="rgba(201,168,76,0.04)" stroke="rgba(201,168,76,0.1)" strokeWidth="1" strokeDasharray="4 4"/>

                {/* Arc 1: Assess → Develop */}
                <path id="pa-arc1" d="M 215,52 Q 340,80 345,220" fill="none" stroke="url(#pa-lg1)" strokeWidth="2.5" markerEnd="url(#pa-am)" strokeLinecap="round" strokeDasharray="300" strokeDashoffset="300" filter="url(#pa-glow)"/>

                {/* Arc 2: Develop → Assist */}
                <path id="pa-arc2" d="M 330,255 Q 200,285 75,255" fill="none" stroke="url(#pa-lg2)" strokeWidth="2.5" markerEnd="url(#pa-am)" strokeLinecap="round" strokeDasharray="300" strokeDashoffset="300" filter="url(#pa-glow)"/>

                {/* Arc 3: Assist → Assess */}
                <path id="pa-arc3" d="M 58,220 Q 60,80 185,52" fill="none" stroke="url(#pa-lg3)" strokeWidth="2.5" markerEnd="url(#pa-am)" strokeLinecap="round" strokeDasharray="300" strokeDashoffset="300" filter="url(#pa-glow)"/>

                {/* Travelling dot */}
                <circle r="5" fill="#c9a84c" filter="url(#pa-glow)" opacity="0">
                  <animateMotion dur="5s" repeatCount="indefinite" begin="2s">
                    <mpath href="#pa-empLoop"/>
                  </animateMotion>
                  <animate attributeName="opacity" values="0;1;1;1;0" keyTimes="0;0.05;0.5;0.95;1" dur="5s" repeatCount="indefinite" begin="2s"/>
                </circle>
                <path id="pa-empLoop" d="M 200,35 Q 340,80 350,235 Q 200,285 50,235 Q 60,80 200,35" fill="none"/>

                {/* Arc insight labels */}
                <text id="pa-lbl1" x="315" y="145" fontSize="9" fill="#a8893a" fontWeight="600" textAnchor="middle" opacity="0">
                  <tspan x="315" dy="0">Skill gaps</tspan>
                  <tspan x="315" dy="13">shape plan</tspan>
                </text>
                <text id="pa-lbl2" x="200" y="295" fontSize="9" fill="#a8893a" fontWeight="600" textAnchor="middle" opacity="0">
                  <tspan x="200" dy="0">Growth fuels</tspan>
                  <tspan x="200" dy="13">knowledge</tspan>
                </text>
                <text id="pa-lbl3" x="82" y="145" fontSize="9" fill="#a8893a" fontWeight="600" textAnchor="middle" opacity="0">
                  <tspan x="82" dy="0">Learning</tspan>
                  <tspan x="82" dy="13">drives re-assess</tspan>
                </text>

                {/* Node: SmartAssess — TOP CENTRE */}
                <g id="pa-node-assess" opacity="0">
                  <circle cx="200" cy="40" r="38" fill="rgba(14,24,32,0.2)" transform="translate(2,3)"/>
                  <circle cx="200" cy="40" r="38" fill="url(#pa-nodeGrad)" stroke="#c9a84c" strokeWidth="2"/>
                  <text x="200" y="36" fontSize="18" textAnchor="middle" dominantBaseline="middle">📊</text>
                  <text x="200" y="56" fontSize="9.5" fill="white" fontWeight="800" textAnchor="middle">SmartAssess</text>
                  <text x="200" y="68" fontSize="7.5" fill="#c9a84c" fontWeight="700" textAnchor="middle" letterSpacing="1">ASSESS</text>
                </g>

                {/* Node: SmartDevelop — BOTTOM RIGHT */}
                <g id="pa-node-develop" opacity="0">
                  <circle cx="355" cy="240" r="38" fill="rgba(14,24,32,0.2)" transform="translate(2,3)"/>
                  <circle cx="355" cy="240" r="38" fill="url(#pa-nodeGrad)" stroke="#c9a84c" strokeWidth="2"/>
                  <text x="355" y="236" fontSize="18" textAnchor="middle" dominantBaseline="middle">🚀</text>
                  <text x="355" y="256" fontSize="9.5" fill="white" fontWeight="800" textAnchor="middle">SmartDevelop</text>
                  <text x="355" y="268" fontSize="7.5" fill="#c9a84c" fontWeight="700" textAnchor="middle" letterSpacing="1">DEVELOP</text>
                </g>

                {/* Node: SmartAssist — BOTTOM LEFT */}
                <g id="pa-node-assist" opacity="0">
                  <circle cx="45" cy="240" r="38" fill="rgba(14,24,32,0.2)" transform="translate(2,3)"/>
                  <circle cx="45" cy="240" r="38" fill="url(#pa-nodeGrad)" stroke="rgba(201,168,76,0.55)" strokeWidth="2" strokeDasharray="5 3"/>
                  <text x="45" y="236" fontSize="18" textAnchor="middle" dominantBaseline="middle">🎧</text>
                  <text x="45" y="256" fontSize="9.5" fill="white" fontWeight="800" textAnchor="middle">SmartAssist</text>
                  <text x="45" y="268" fontSize="7.5" fill="#c9a84c" fontWeight="700" textAnchor="middle" letterSpacing="1">ASSIST</text>
                  {/* Early Access badge */}
                  <rect x="21" y="193" width="52" height="14" rx="7" fill="#c9a84c"/>
                  <text x="45" y="204" fontSize="6.5" fill="#1c2b3a" fontWeight="900" textAnchor="middle">EARLY ACCESS</text>
                </g>
              </svg>
            </div>

            <div className="pa-caption" id="pa-caption">
              <span>Every employee. Every day. Continuously improving.</span>
            </div>
          </div>
        </div>

        {/* Global statement */}
        <p className="pa-global-note">
          PossoBuild sits above your existing HR systems — turning workforce data into actionable intelligence.
        </p>
      </div>
    </section>
  )
}
